import React, { useMemo, useState } from "react";
import { useDebouncedApiEffect } from "../../hooks/useDebouncedApiEffect";
import { GetMentoredStudentsCountCancelToken } from "../../services/DashBoardServices";
import { generateFetchDataPayload, getDigitClass, MAX_DASHBOARD_RETRY_ATTEMPTS, triggerExponentialBackoff } from "./util";

export default function MentoredStudentsBadge({
    filterIntake,
    filterLearningMethod,
    filterLecturer,
    filterSchool
}) {
    const [mentoredCount, setMentoredCount] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [retryFetchCount, setRetryCount] = useState(0);

    const showLoader = isLoading ||
        (mentoredCount?.pending_sign_off ?? null) === null &&
        (mentoredCount?.notes_pending_response ?? null) === null;

    const fetchMentoredCount = async ({ cancelToken }) => {
        if (retryFetchCount >= MAX_DASHBOARD_RETRY_ATTEMPTS) {
            console.log("MentoredStudentsBadge - Max retries reached, skipping fetch");
            return;
        }

        const data = generateFetchDataPayload({
            filterIntake,
            filterSchool,
            filterLearningMethod,
            filterLecturer
        });

        try {
            setIsLoading(true);
            const res = await GetMentoredStudentsCountCancelToken(data, cancelToken);
            setMentoredCount(res?.data);
            setIsLoading(false);
        } catch (error) {
            console.error(`MentoredStudentsBadge - Attempt ${retryFetchCount + 1} failed`, error);
            if (retryFetchCount < MAX_DASHBOARD_RETRY_ATTEMPTS - 1) {
                triggerExponentialBackoff(retryFetchCount, setRetryCount);
            } else {
                setRetryCount(MAX_DASHBOARD_RETRY_ATTEMPTS);
                setIsLoading(false);
            }
        }
    };

    useDebouncedApiEffect(fetchMentoredCount, [
        filterIntake,
        filterLearningMethod,
        filterLecturer,
        filterSchool,
        retryFetchCount
    ]);

    // Memoized class calculations
    const pendingSignOffClass = useMemo(() => getDigitClass(mentoredCount?.pending_sign_off), [mentoredCount?.pending_sign_off]);
    const notesPendingClass = useMemo(() => getDigitClass(mentoredCount?.notes_pending_response), [mentoredCount?.notes_pending_response]);

    if (retryFetchCount === MAX_DASHBOARD_RETRY_ATTEMPTS && !isLoading) {
        // Max retries reached, return nothing
        return "";
    }

    if (showLoader) {
        return (
            <span className="dashboard-loading">
                <i className="fas fa-cog fa-spin"></i>
            </span>
        );
    }

    return (
        <>
            <span className="nav-fa-comments" title="Students Pending Sign-off">
                <small className={`notify-count ${pendingSignOffClass}`}>
                    {mentoredCount?.pending_sign_off ?? 0}
                </small>
                <i className="fal fa-calendar comments"></i>
            </span>

            <span className="nav-fa-comments ml-3" title="Notes Pending Response">
                <small className={`notify-count ${notesPendingClass}`}>
                    {mentoredCount?.notes_pending_response ?? 0}
                </small>
                <i className="fal fa-comment-alt-dots comments"></i>
            </span>
        </>
    );
}
